<script lang="ts">
	// Enhanced page component to handle special cases from server
	// @ts-nocheck
	import { onMount } from 'svelte';
	import Button from '$lib/components/ui/button/button.svelte';
	import { CalendarIcon, MapPin } from 'lucide-svelte';

	import * as Card from '$lib/components/ui/card';

	import Featured from '$lib/components/home/Featured.svelte';

	// Import will be needed in future implementation
	// import { browser } from '$app/environment';

	import { trip } from '$lib/utils/tripStore';
	import { toast } from 'svelte-sonner';
	import { enhancedImages } from '$lib/utils/images';

	// Function to check if an image URL is in the enhancedImages collection
	function isEnhancedImage(imageUrl) {
		if (!imageUrl) return false;

		// Only consider local file paths as potential enhanced images
		// External URLs (containing http:// or https://) are not enhanced
		if (imageUrl.includes('http://') || imageUrl.includes('https://')) {
			return false;
		}

		// Try to find the image in our enhanced images collection
		try {
			return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		} catch (err) {
			console.warn('Error checking enhanced image:', err);
			return false;
		}
	}

	// Function to get the appropriate image source
	function getImageSource(imageUrl) {
		if (!imageUrl) return null;

		try {
			// If it's an enhanced image, get it from the enhancedImages collection
			const enhancedEntry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
			if (enhancedEntry && enhancedEntry[1] && enhancedEntry[1].default) {
				return enhancedEntry[1].default;
			}
		} catch (err) {
			console.warn('Error getting enhanced image source:', err);
		}

		// Otherwise, use the URL directly
		return imageUrl;
	}

	// Function to restore original image URLs after build
	function restoreOriginalImageUrls() {
		if (!tour || !tour.images) return;

		// Check each image for stored original URLs (from server-side)
		tour.images.forEach((img) => {
			if (img._originalUrl) {
				// Restore the original URL that was replaced during build
				img.itemUrl = img._originalUrl;
				delete img._originalUrl;
			}
		});
	}

	// On component mount, restore original image URLs
	onMount(() => {
		restoreOriginalImageUrls();
	});

	import { tourContent } from '$lib/utils/tourContent';
	import Gallery from '$lib/components/tours/Gallery.svelte';
	import DayTour from '$lib/components/tours/DayTour.svelte';
	import MultiDay from '$lib/components/tours/MultiDay.svelte';
	// Availability component will be used in future updates
	import Seo from '$lib/components/SEO.svelte';

	// Dynamic image imports
	interface Props {
		data: import('./$types').PageData;
	}

	let { data }: Props = $props();

	// Call restoreOriginalImageUrls on mount to fix any placeholders
	onMount(() => {
		restoreOriginalImageUrls();
		window.scrollTo(0, 0);
	});

	const allTours = [
		// Day Tours
		{
			productCode: 'P18ZC5',
			tourName: 'Bruny Island Day Tour',
			rezdyLink: 'https://tassietours.rezdy.com/188932/bruny-island-day-tour'
		},
		{
			productCode: 'PJHLE0',
			tourName: 'Port Arthur Shuttle and Tasman Sights',
			rezdyLink: 'https://tassietours.rezdy.com/262968/port-arthur-shuttle-and-tasman-sights'
		},
		{
			productCode: 'PNVP8M',
			tourName: 'Hobart Highlights Day Tour',
			rezdyLink: 'https://tassietours.rezdy.com/211336/hobart-highlights-day-tour'
		},
		{
			productCode: 'PQ5CJC',
			tourName: 'Mount Field and the Derwent Tasting Trail',
			rezdyLink: 'https://tassietours.rezdy.com/651177/mount-field-and-the-derwent-tasting-trail'
		},
		{
			productCode: 'PQKW4C',
			tourName: 'Nipaluna Hobart Highlights',
			rezdyLink: 'https://tassietours.rezdy.com/534771/nipaluna-hobart-highlights'
		},
		{
			productCode: 'PP0SGV',
			tourName: 'Cradle Mountain in a day from Hobart',
			rezdyLink: 'https://tassietours.rezdy.com/288270/cradle-mountain-in-a-day-from-hobart'
		},
		{
			productCode: 'PMPLBM',
			tourName: 'kunanyi/Mt Wellington Shuttle',
			rezdyLink: 'https://tassietours.rezdy.com/623968/kunanyi-mt-wellington-shuttle'
		},
		{
			productCode: 'PTC0KJ',
			tourName: 'Hastings Caves, Tahune Airwalk and the Huon testing trail',
			rezdyLink:
				'https://tassietours.rezdy.com/661924/hastings-caves-tahune-airwalk-and-the-huon-tasting-trail'
		},
		{
			productCode: 'PNNK81',
			tourName: 'A Day At Wineglass Bay',
			rezdyLink: 'https://tassietours.rezdy.com/94219/a-day-at-wineglass-bay'
		},
		{
			productCode: 'PCBKLS',
			tourName: 'Cradle Mountain Explorer: Hobart to Launceston',
			rezdyLink:
				'https://tassietours.rezdy.com/521312/cradle-mountain-explorer-hobart-to-launceston'
		},
		{
			productCode: 'PHJFAH',
			tourName: 'A Day At Wineglass Bay - Departing Launceston',
			rezdyLink: 'https://tassietours.rezdy.com/710452/a-day-at-wineglass-bay-departing-launceston'
		},

		// Multi-Day Tours
		{
			productCode: 'PTNCNK',
			tourName:
				'4 Day Tasmanian Highlights Bundle - Hobart, Port Arthur, Bruny Island, Wildlife & Mt Field',
			rezdyLink:
				'https://tassietours.rezdy.com/660420/4-day-tasmanian-highlights-bundle-hobart-port-arthur-bruny-island-hastings-and-tahune'
		},
		{
			productCode: 'PHKZNS',
			tourName: '3 Day Tasmanian Highlights Bundle 1 - Hobart, Port Arthur and Bruny Island',
			rezdyLink:
				'https://tassietours.rezdy.com/322300/3-https://tassietours.rezdy.com/322300/3-day-tasmanian-highlights-bundle-1-hobart-port-arthur-and-bruny-island-tasmanian-highlights-bundle-1-hobart-port-arthur-and-bruny-island'
		},
		{
			productCode: 'PHKZNS',
			tourName: '3 Day Tasmanian Highlights Bundle 1 - Hobart, Port Arthur and Bruny Island',
			rezdyLink:
				'https://tassietours.rezdy.com/322300/3-https://tassietours.rezdy.com/322300/3-day-tasmanian-highlights-bundle-1-hobart-port-arthur-and-bruny-island-tasmanian-highlights-bundle-1-hobart-port-arthur-and-bruny-island'
		},
		{
			productCode: 'P6GE12',
			tourName: '2 day 1',
			rezdyLink:
				'https://tassietours.rezdy.com/209693/2-day-tasmanian-highlights-bundle-2-hobart-highlights-bruny-island'
		},
		{
			productCode: 'P1AHF0',
			tourName: '2 day 2',
			rezdyLink:
				'https://tassietours.rezdy.com/373491/2-day-tasmanian-highlights-bundle-3-bruny-island-port-arthur'
		},

		{
			productCode: 'P0V9R2',
			tourName: "Tasmania's most iconic in 11 days",
			rezdyLink: 'https://tassietours.rezdy.com/570292/tasmania-s-most-iconic-in-11-days'
		},
		{
			productCode: 'PVHGUQ',
			tourName: 'Roaming Tasmania for 8 days',
			rezdyLink: 'https://tassietours.rezdy.com/579518/roaming-tasmania-for-8-days'
		},
		{
			productCode: 'PTWH1T',
			tourName: 'Thrillmania - 7 days of adrenaline',
			rezdyLink: 'https://tassietours.rezdy.com/581788/thrillmania-7-days-of-adrenaline'
		},
		{
			productCode: 'PU9VRB',
			tourName: '3 Day Tasmanian Highlights Tour - Bruny Island & Mt Field',
			rezdyLink:
				'https://tassietours.rezdy.com/660447/2-day-tasmanian-highlights-tour-bruny-island-mt-field'
		},
		{
			productCode: 'PM5WCG',
			tourName:
				'5 Day Island Icons Day Tour Bundle - Cradle Mountain, Wineglass Bay, Port Arthur, Bruny Island, and Hobart highlights',
			rezdyLink:
				'https://tassietours.rezdy.com/662219/5-day-island-icons-day-tour-bundle-cradle-mountain-wineglass-bay-port-arthur-bruny-island-and-hobart-highlights'
		},
		{
			productCode: 'PBRPKL',
			tourName:
				'6 Day Island Icons Day Tour Bundle - Cradle Mountain, Wineglass Bay, Hobart Highlights, Port Arthur, Bruny Island, Hastings and Tahune',
			rezdyLink:
				'https://tassietours.rezdy.com/711040/6-day-island-icons-day-tour-bundle-cradle-mountain-wineglass-bay-hobart-highlights-port-arthur-bruny-island-hastings-and-tahune'
		},
		{
			productCode: 'PKQPM9',
			tourName: '3 Day Tasmanian Highlights Bundle 2 - Hobart, Wineglass Bay and Bruny Island',
			rezdyLink:
				'https://tassietours.rezdy.com/710980/3-day-tasmanian-highlights-bundle-2-hobart-wineglass-bay-and-bruny-island'
		},
		{
			productCode: 'P0QKC2',
			tourName: '2 Day 1 night - Cradle Mt & Wineglass Bay: Hobart to Launceston',
			rezdyLink:
				'https://tassietours.rezdy.com/698854/2-day-1-night-cradle-mt-wineglass-bay-overnight-in-launceston'
		},

		// Shore Excursions
		{
			productCode: 'PM0VRV',
			tourName: 'Shore Excursion - nipaluna (Hobart) Highlights',
			rezdyLink: 'https://tassietours.rezdy.com/571853/shore-excursion-nipaluna-hobart-highlights'
		},
		{
			productCode: 'P0J8QR',
			tourName: 'Shore Excursion - Hobart Highlights Day Tour',
			rezdyLink: 'https://tassietours.rezdy.com/264285/shore-excursion-hobart-highlights-day-tour'
		},
		{
			productCode: 'PB2LG2',
			tourName: "Shore Excursion - Hobart's best in half a day.",
			rezdyLink: 'https://tassietours.rezdy.com/276409/shore-excursion-hobart-s-best-in-half-a-day'
		},
		{
			productCode: 'PSP9WG',
			tourName: 'Shore Excursion Port Arthur Day Tour',
			rezdyLink: 'https://tassietours.rezdy.com/536757/shore-excursion-port-arthur-day-tour'
		},
		{
			productCode: 'P9TVKQ',
			tourName: 'Shore Excursion - Bruny Island day tour',
			rezdyLink: 'https://tassietours.rezdy.com/278655/shore-excursion-bruny-island-day-tour'
		},
		{
			productCode: 'PKLVUQ',
			tourName: 'Shore Excursion - Cradle Mountain Shuttle Bus',
			rezdyLink: 'https://tassietours.rezdy.com/191634/shore-excursion-cradle-mountain-shuttle-bus'
		},
		{
			productCode: 'PHBQ6G',
			tourName: 'Shore Excursion - Port Arthur - Tasman Peninsula',
			rezdyLink: 'https://tassietours.rezdy.com/263144/shore-excursion-port-arthur-tasman-peninsula'
		},
		// PMPLBM is listed in Day Tours section above and also in Archive Shore Excursions
		{
			productCode: 'PQ4FDL',
			tourName: 'Shore Excursion Mount Field & Derwent Valley Tasting Trail',
			rezdyLink:
				'https://tassietours.rezdy.com/668711/shore-excursion-mount-field-derwent-valley-tasting-trail'
		},

		// Private Tours
		{
			productCode: 'P80WBP',
			tourName: 'Private Tour Package 1 - Hobart, Bruny Island, Port Arthur',
			rezdyLink:
				'https://tassietours.rezdy.com/606850/private-tour-package-1-hobart-bruny-island-port-arthur'
		},
		{
			productCode: 'PUYDUT',
			tourName:
				'Private Tour Package 2 - Hobart, Bruny Island, Mt Field with Bonorong Wildlife Sanctuary',
			rezdyLink:
				'https://tassietours.rezdy.com/606857/private-tour-package-2-hobart-bruny-island-mt-field-with-bonorong-wildlife-sanctuary'
		},
		{
			productCode: 'PMFU3K',
			tourName:
				'Private Tour Package 3 - Hobart, Bruny Island, Freycinet National Park, Port Arthur',
			rezdyLink:
				'https://tassietours.rezdy.com/600152/private-tour-package-3-hobart-bruny-island-freycinet-national-park-port-arthur'
		},
		{
			productCode: 'PVSUAB',
			tourName: 'Private Tour Package 4',
			rezdyLink:
				'https://tassietours.rezdy.com/600155/private-tour-package-4-bruny-island-freycinet-national-park-cradle-mountain-launceston'
		},
		// PVSUAB is listed in Multi-Day Tours section above and also in Archive Private Tours
		{
			productCode: 'P0QZS7',
			tourName:
				'Private Tour Package 5 - Hobart, Bruny Island, Freycinet National Park, St Helens and Bay of Fire, Cradle Mountain',
			rezdyLink:
				'https://tassietours.rezdy.com/600158/private-tour-package-5-hobart-bruny-island-freycinet-national-park-st-helens-and-bay-of-fire-cradle-mountain'
		},
		{
			productCode: 'PXA0PL',
			tourName:
				'Private Tour Package 6 - Hobart, Bruny Island, Mt Field National Park, Freycinet National Park, Cradle Mountain',
			rezdyLink:
				'https://tassietours.rezdy.com/600994/private-tour-package-6-hobart-bruny-island-mt-field-national-park-freycinet-national-park-cradle-mountain'
		},
		{
			productCode: 'PKFPUC',
			tourName:
				'Private Tour Package 7 - Hobart, Bruny Island, Port Arthur, Mt Field National Park, Freycinet National Park, Cradle Mountain',
			rezdyLink:
				'https://tassietours.rezdy.com/600998/private-tour-package-7-hobart-bruny-island-port-arthur-mt-field-national-park-freycinet-national-park-cradle-mountain'
		},
		{
			productCode: 'P9AM50',
			tourName:
				'Private Tour Package 8 - Hobart, Bruny Island, Port Arthur, Mt Field National Park, Freycinet National Park, Cradle Mountain',
			rezdyLink:
				'https://tassietours.rezdy.com/600999/private-tour-package-8-hobart-bruny-island-port-arthur-mt-field-national-park-freycinet-national-park-cradle-mountain'
		},
		{
			productCode: 'PLQWG9',
			tourName: 'Private Tour of Hobart and surrounds',
			rezdyLink: 'https://tassietours.rezdy.com/567554/private-tour-of-hobart-and-surrounds'
		}
	];

	// Access the tour data directly from the 'data' prop
	const tour = data.tour;
	// These will be used in future improvements when we implement the Availability component
	// let availabilityData = data.availabilityData;
	// let pickupData = data.pickupData?.pickupLocations || [];

	// Handle the case where the tour is not found
	if (!tour) {
		throw new Error('Tour not found'); // This will trigger a 404 error page
	}

	// Client-side refresh of availability data will be implemented in the future
	// let isLoadingData = false;

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);

		// Refresh availability data on the client side - will be used in future implementation
		/*
		if (browser) {
			refreshAvailabilityData();
		}
		*/
	});

	// Function to refresh availability data from the API - will be used in future implementation
	/*
	async function refreshAvailabilityData() {
		if (!browser || isLoadingData) return;

		isLoadingData = true;
		try {
			// Get date range for availability
			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth() + 1;
			const startDate = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`;
			const endDate = new Date(currentYear + 1, currentMonth, 0).getDate();
			const endDateString = `${currentYear + 1}-${currentMonth.toString().padStart(2, '0')}-${endDate}`;

			// Get API key
			const apiKey = import.meta.env.VITE_REZDY_PROD;

			// Fetch latest availability data
			const availabilityUrl = `https://api.rezdy.com/v1/availability?apiKey=${apiKey}&productCode=${tour.productCode}&startTimeLocal=${startDate} 00:00:00&endTimeLocal=${endDateString} 00:00:00`;
			const pickupUrl = `https://api.rezdy.com/v1/products/${tour.productCode}/pickups?apiKey=${apiKey}`;

			const [availabilityResponse, pickupResponse] = await Promise.all([
				fetch(availabilityUrl),
				fetch(pickupUrl)
			]);

			if (availabilityResponse.ok) {
				availabilityData = await availabilityResponse.json();
			}

			if (pickupResponse.ok) {
				const pickupResult = await pickupResponse.json();
				if (pickupResult && pickupResult.pickupLocations) {
					pickupData = pickupResult.pickupLocations;
				}
			}
		} catch (err) {
			console.error('Error refreshing availability data:', err);
		} finally {
			isLoadingData = false;
		}
	}
	*/

	function formatDuration(durationMinutes) {
		const hours = Math.ceil(durationMinutes / 60); // Round up to nearest hour
		if (hours >= 24) {
			const days = Math.ceil(hours / 24); // Round up to nearest day
			return `${days} day${days > 1 ? 's' : ''}`;
		} else {
			return `${hours} hour${hours > 1 ? 's' : ''}`;
		}
	}

	function formatPrice(price) {
		return `$${price.toFixed(2)}`;
	}

	// Function moved to a utility file to be imported when needed

	function formatLocation(locationAddress) {
		if (locationAddress && locationAddress.city && locationAddress.state) {
			return `${locationAddress.city}, ${locationAddress.state}`;
		} else {
			return 'Location unavailable'; // Or any other suitable default message
		}
	}
	function addToTrip() {
		trip.update((currentTrips) => {
			const isAlreadyInTrip = currentTrips.some((t) => t.productCode === tour.productCode);

			if (isAlreadyInTrip) {
				toast.error(`${tour.name} is already in your trip`);
				return currentTrips;
			}

			toast.success(`${tour.name} successfully added to trip`);
			return [...currentTrips, tour];
		});
	}

	function handleAnchorClick(event) {
		event.preventDefault();
		const link = event.currentTarget;
		const anchorId = new URL(link.href).hash.replace('#', '');
		const anchor = document.getElementById(anchorId);
		window.scrollTo({
			top: anchor.offsetTop,
			behavior: 'smooth'
		});
	}
</script>

<Seo
	title="{tour.name} - Tassie Tours"
	description={tour.shortDescription}
	keywords="Tasmania Tours, Tasmania local tour, Tassie Tours, Day tours from Hobart, Discover Bruny Island, Cradle Mountain, Active Tasmania tours"
	siteName="Tassie Tours"
	imageURL={tour.images[0].itemUrl}
	logo="/logo/logo-black.png"
	author="Tassie Tours"
	type="Tour"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
	socials={[]}
/>

<!-- HERO -->
<section class="relative mx-auto flex h-[600px] flex-col items-center justify-center">
	{#if tour.images && tour.images.length > 0}
		{#if tour.images[0] && isEnhancedImage(tour.images[0].itemUrl)}
			<enhanced:img
				src={getImageSource(tour.images[0].itemUrl)}
				alt={tour.name}
				class="absolute inset-0 h-[600px] w-full object-cover"
				sizes="(min-width: 1280px) 1280px, 100vw"
				fetchpriority="high"
			/>
		{:else}
			<img
				src={tour.images[0].itemUrl}
				alt={tour.name || 'Tour Image'}
				loading="eager"
				class="absolute inset-0 h-full w-full object-cover"
				onerror={(e) => {
					console.warn('Image failed to load:', tour.images[0].itemUrl);
					e.currentTarget.onerror = null;
					e.currentTarget.src = '/images/placeholder-tour.jpg';
				}}
			/>
		{/if}
	{:else}
		<div class="absolute inset-0 h-[600px] w-full bg-muted"></div>
	{/if}

	<div class="absolute inset-0 bg-foreground/20"></div>

	<div
		class="pointer-events-none absolute inset-0 mx-auto flex h-[600px] w-full max-w-7xl flex-col justify-center px-4"
	>
		<h1
			class="mx-auto max-w-2xl scroll-m-20 text-center font-heading text-2xl font-bold tracking-tight text-background lg:mx-0 lg:text-left lg:text-4xl"
		>
			{tour.name}
		</h1>
		<div class="mx-auto mt-6 flex items-center lg:mx-0">
			<p class=" flex flex-col font-heading text-xl font-bold text-background lg:text-3xl">
				<span class="font-sans text-xs">From AUD </span>
				{tour.advertisedPrice > 1000
					? `$${tour.advertisedPrice.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1 ')}`
					: formatPrice(tour.advertisedPrice)}
			</p>
		</div>
		<div class="mx-auto flex gap-4 pt-12 font-medium text-background lg:mx-0">
			<div class="flex items-center">
				<CalendarIcon class="mr-2 h-6 w-6 stroke-background/80 " />
				<p class="lg:text-md text-sm">{formatDuration(tour.durationMinutes)}</p>
			</div>
			<div class="flex items-center">
				<MapPin class="mr-2 h-6 w-6 stroke-background/80 " />
				<p class="lg:text-md text-sm">{formatLocation(tour.locationAddress)}</p>
			</div>
		</div>
		<div class="mx-auto flex flex-col gap-4 md:flex-row lg:mx-0">
			<Button
				data-sveltekit-noscroll
				href="#book"
				onclick={handleAnchorClick}
				class="pointer-events-auto mx-auto mt-8 w-44 rounded-full bg-tt-red px-6 text-lg font-semibold hover:bg-tt-red/90 lg:mx-0"
				>Book now</Button
			>
			<Button
				variant="outline"
				onclick={addToTrip}
				data-sveltekit-noscroll
				class="pointer-events-auto mx-auto w-44 rounded-full border-2 bg-transparent px-6 text-lg font-semibold text-background md:mt-8  lg:mx-0"
				>Add to trip</Button
			>
		</div>
	</div>
</section>

<!-- GALLERY -->
<Gallery {tour} />

<!-- CONTENT & BOOKING -->
<section class="mx-auto flex max-w-7xl flex-col px-4 pb-12 lg:pb-20 lg:pt-20">
	<div class="flex w-full flex-col lg:grid lg:grid-cols-4 lg:gap-4">
		{#if tourContent.some((t) => t.productCode === tour.productCode)}
			<div class="col-span-3">
				<h2
					class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
				>
					{tourContent.find((t) => t.productCode === tour.productCode)?.headline}
				</h2>
				<div class="mt-6" id="tourDescription">
					<!-- svelte-ignore a11y-no-html-content -->
					<!-- Using sanitized content from CMS - HTML is trusted as it's from our own database -->
					{@html tourContent.find((t) => t.productCode === tour.productCode)?.description}
				</div>
			</div>
		{:else}
			<div class="col-span-3">
				<h2
					class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
				>
					Explore Tasmania - Tour Description
				</h2>
				<div class="mt-6" id="tourDescription">
					<!-- svelte-ignore a11y-no-html-content -->
					<!-- HTML is trusted as it's from our own verified database -->
					{@html tour.description}
				</div>
			</div>
		{/if}

		<!-- <Availability {tour} {availabilityData} {pickupData} /> -->

		<!-- TEMPORARY SOLUTIONS -->
		<div id="book" class="col-span-1 pt-16">
			<Card.Root class="mx-auto rounded-2xl">
				<Card.Header>
					<Card.Title class="font-heading text-xl">Book now</Card.Title>
					<Card.Description
						>Click below and enter your information to secure your booking</Card.Description
					>
				</Card.Header>
				<Card.Content>
					<div class="grid gap-4">
						<Button
							target="_blank"
							href={allTours.find((t) => t.productCode === tour.productCode)?.rezdyLink}
							class="button-booking rezdy rezdy-modal w-full rounded-full bg-tt-red hover:bg-tt-red/80"
						>
							Book Now
						</Button>

						<Button onclick={addToTrip} variant="outline" class="w-full rounded-full"
							>Add to trip</Button
						>
					</div>
					<div class="mt-4 text-center text-sm">
						<span class="underline">Please note:</span> After your purchase is confirmed we will email
						you a confirmation.
					</div>
				</Card.Content>
			</Card.Root>
		</div>
	</div>
</section>

{#if tour.productType === 'DAYTOUR'}
	<DayTour {tour} />
{:else if tour.productType === 'MULTIDAYTOUR'}
	<MultiDay
		{tour}
		rezdyLink={allTours.find((t) => t.productCode === tour.productCode)?.rezdyLink}
	/>
{/if}

{#if tour.productType === 'DAYTOUR'}
	<Featured
		heading="Click or swipe to explore more day tours"
		selected="dayTours"
		productCode={tour.productCode}
	/>
{:else if tour.productType === 'MULTIDAYTOUR'}
	<Featured
		heading="Click or swipe to explore more multi-day tours"
		selected="multiDayTours"
		productCode={tour.productCode}
	/>
{:else if tour.productType === 'SHOREEXCURSION'}
	<Featured
		heading="Click or swipe to explore more shore excursions"
		selected="shoreExcursions"
		productCode={tour.productCode}
	/>
{:else if tour.productType === 'PRIVATETOUR'}
	<Featured
		heading="Click or swipe to explore more private tours"
		selected="privateTours"
		productCode={tour.productCode}
	/>
{/if}
